/**
 * The one lexical scorer.
 *
 * Everything that ranks anything against a typed query goes through this file: the ⌘K
 * palette's doc results, the palette's navigation commands, and the docs index filter on
 * /docs. There is deliberately no second implementation anywhere — the surface this
 * replaces had its scorer hand-copied into the palette and the index page, and the two
 * drifted until the same query ranked differently depending on where you typed it. A
 * ranking rule is a product decision; it gets one home.
 *
 * The scorer is lexical, not semantic: it matches the words you typed against the fields
 * the publish step extracted (title, tags, headings, excerpt). It does not read guide
 * bodies — those live in a lazily-loaded chunk (see docs/bodies.ts) and pulling them here
 * would put every published guide into the bundle that loads on the certificates page.
 * Semantic retrieval over full bodies is Epic D5's GCP Agent Search, not this.
 */
import type { DocEntry, DocHeading } from 'docs'
import type { GlossaryEntry } from 'docs/glossary'

/**
 * How well a needle matches a haystack, 0 (no match) to 1 (identical).
 *
 * The tiers exist because substring matching alone ranks badly: searching "own" would
 * score "Ownership transfer" and "Downtime" identically, when only the first is
 * plausibly what you meant. Rewarding a match that starts a word separates them without
 * needing a tokeniser or a stemmer.
 */
const rawStrength = (h: string, n: string): number => {
  if (h === n) return 1
  if (h.startsWith(n)) return 0.9

  const at = h.indexOf(n)
  if (at === -1) return 0

  // A match that begins a word ("transfer" in "Ownership transfer") is a far better
  // signal than one buried mid-word ("own" in "Downtime").
  const before = h[at - 1]
  const startsWord = before !== undefined && !/[a-z0-9]/i.test(before)
  return startsWord ? 0.75 : 0.4
}

/**
 * Discount applied when a match is only found after folding a plural.
 *
 * Small, but non-zero: a literal match should still outrank a folded one, so searching
 * "certificates" puts a page actually called "Certificates" above "Certificate Scans".
 */
const PLURAL_PENALTY = 0.9

export const matchStrength = (haystack: string, needle: string): number => {
  if (!needle) return 0
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase()

  const direct = rawStrength(h, n)
  if (direct > 0) return direct

  /**
   * One-step plural folding, both directions.
   *
   * Without it "certificates" does not match "Certificate Scans" and "scan" does not
   * match "Scans" — which was the single biggest gap when this was run against the real
   * corpus, because guide titles are inconsistently singular. This is not a stemmer and
   * is not trying to be: no Porter rules, no irregulars, no dictionary. Just the "s" that
   * accounts for nearly all of the misses, guarded on length so "as"/"is" are untouched.
   */
  if (n.length > 3 && n.endsWith('s')) {
    const singular = n.slice(0, -1)
    const folded = rawStrength(h, singular)
    if (folded > 0) return folded * PLURAL_PENALTY
  }
  if (n.length > 2 && !n.endsWith('s')) {
    const folded = rawStrength(h, `${n}s`)
    if (folded > 0) return folded * PLURAL_PENALTY
  }

  return 0
}

/** A weighted piece of text a query can match against. */
export interface ScorableField {
  text: string
  weight: number
}

/**
 * Words that say something about the SHAPE of a question and nothing about its subject.
 *
 * They have to go, and the reason is the AND rule below rather than tidiness: a term that
 * matches nothing rejects the whole record, so one stray "what" is enough to delete the
 * correct answer from the results entirely. That is not hypothetical — `what is a
 * certificate` used to return the glossary entry for *Encode*, whose one-sentence
 * definition happens to read "…which is what binds that specific tag…" and therefore
 * contained all four of what/is/a/certificate, while the *Certificate* entry contained no
 * "what" and scored zero. The glossary was worse at questions than at bare nouns, which is
 * backwards: a question is the least ambiguous statement of definitional intent there is.
 *
 * Kept deliberately short. Every word here is one a user can no longer search for as a
 * word, so the bar is "carries no retrieval signal in any query", not "common".
 */
const STOPWORDS: ReadonlySet<string> = new Set([
  // determiners
  'a', 'an', 'the',
  // copulas and auxiliaries
  'is', 'are', 'was', 'were', 'be', 'am', 'do', 'does', 'did', 'can', 'could', 'should',
  'would', 'will',
  // interrogatives
  'what', 'whats', "what's", 'how', 'why', 'when', 'where', 'which', 'who', 'whose',
  // pronouns
  'i', 'me', 'my', 'we', 'our', 'you', 'your', 'it', 'its', 'they', 'them',
  // glue
  'of', 'for', 'to', 'in', 'on', 'at', 'by', 'with', 'from', 'about', 'and', 'or',
  'that', 'this', 'these', 'those',
  // verbs of asking
  'tell', 'define', 'explain', 'mean', 'means', 'meaning', 'definition', 'show',
])

/**
 * Openings that mark a query as a QUESTION, and whether that question wants a definition.
 *
 * The `definitional` flag is the whole reason this is a table rather than one regex.
 * "what is a tag" and "how do I encode a tag" both need their opening stripped before
 * scoring, but only the first is asking what a word MEANS — so only the first should push
 * the glossary above the navigation rows (see the palette). Treating every question as
 * definitional would answer "how do I buy tags" with a dictionary entry.
 *
 * Order matters: the longer, more specific pattern has to be tried before the shorter one
 * that prefixes it, or `what's new` would be consumed by the `what … is` branch.
 */
const INTERROGATIVE_OPENINGS: readonly { readonly pattern: RegExp; readonly definitional: boolean }[] = [
  { pattern: /^what(?:'s|s)?\s+(?:is|are|does|do)\b/i, definitional: true },
  { pattern: /^what(?:'s|s)\b/i, definitional: true },
  { pattern: /^(?:definition|meaning)\s+of\b/i, definitional: true },
  { pattern: /^(?:define|explain)\b/i, definitional: true },
  { pattern: /^tell\s+me\s+about\b/i, definitional: true },
  { pattern: /^how\s+to\b/i, definitional: false },
  { pattern: /^how\s+(?:do|does|can|should)\s+(?:i|you|we)\b/i, definitional: false },
  { pattern: /^how\s+(?:do|does|can|should)\b/i, definitional: false },
  { pattern: /^where\s+(?:is|are)\b/i, definitional: false },
  { pattern: /^where\s+(?:do|can)\s+(?:i|you|we)\b/i, definitional: false },
  { pattern: /^(?:can|should)\s+i\b/i, definitional: false },
  { pattern: /^show\s+me\b/i, definitional: false },
]

/** Trim leading/trailing punctuation so "tag?" is the same term as "tag". */
const stripEdgePunctuation = (token: string): string =>
  token.replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^\p{L}\p{N}]+$/u, '')

const splitTerms = (text: string): string[] =>
  text
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(stripEdgePunctuation)
    .filter(Boolean)

/** A query reduced to the words that actually say what is being looked for. */
export interface NormalizedQuery {
  /** The text that gets scored — used for terms AND for the phrase bonus. */
  text: string
  /** `text` split into terms. */
  terms: string[]
  /** The raw query asked for a MEANING ("what is a tag", "define scan"). */
  definitional: boolean
  /** Normalisation actually removed something. */
  changed: boolean
}

/**
 * Reduce a typed query to its content words, and say whether it was a definitional question.
 *
 * Two passes, in this order: strip a recognised interrogative opening, then drop stopwords.
 * Both are guarded the same way — **a pass that would leave nothing is not applied**. That
 * guard is what keeps normalisation from being able to make any query worse:
 *
 *   - `what is`      → nothing survives the strip, so the raw query is scored.
 *   - `a`            → every token is a stopword, so the raw token is scored, and a product
 *                      or page whose name contains a bare "a" or "is" is still reachable.
 *   - `what's new`   → the opening goes, "new" remains, and the What's-new guide still wins
 *                      on its title. The normaliser cannot eat a query down to nothing.
 *
 * Deliberately NOT max(rawScore, normalisedScore). Scoring both and taking the best sounds
 * safer but keeps the junk: `what is a certificate` would still surface Encode at rank 2,
 * scored on words we have already decided are not content. Once the opening is recognised,
 * those words are known noise and using them for anything is a mistake. The property this
 * buys is worth stating: **a definitional query and its bare noun produce the same ranking**
 * — `what is a certificate` and `certificate` are the same search, which is exactly what a
 * user typing the longer one believes.
 */
export const normalizeQuery = (raw: string): NormalizedQuery => {
  const trimmed = raw.trim()
  const rawTerms = splitTerms(trimmed)

  let body = trimmed
  let definitional = false

  for (const opening of INTERROGATIVE_OPENINGS) {
    if (!opening.pattern.test(body)) continue
    const remainder = body.replace(opening.pattern, '').trim()
    // Never strip an opening down to nothing — "what is" on its own is then scored as
    // typed rather than as an empty query.
    if (splitTerms(remainder).length === 0) break
    body = remainder
    definitional = opening.definitional
    break
  }

  const bodyTerms = splitTerms(body)
  const content = bodyTerms.filter(t => !STOPWORDS.has(t))
  const terms = content.length > 0 ? content : bodyTerms

  return {
    text: terms.join(' '),
    terms,
    definitional,
    changed: terms.length !== rawTerms.length || terms.join(' ') !== rawTerms.join(' '),
  }
}

/**
 * One-entry memo.
 *
 * `scoreFields` is called once per record per keystroke — a few hundred times across the
 * docs, glossary and command catalogs — and every one of those calls normalises the SAME
 * string. The cache is one slot because the access pattern is a single query fanned out
 * over a corpus; a bigger one would never be hit.
 */
let lastRaw: string | undefined
let lastNormalized: NormalizedQuery | undefined

const normalizeCached = (raw: string): NormalizedQuery => {
  if (raw === lastRaw && lastNormalized) return lastNormalized
  lastRaw = raw
  lastNormalized = normalizeQuery(raw)
  return lastNormalized
}

/** True when the query asked what something MEANS, rather than naming it or asking how. */
export const isDefinitionalQuery = (query: string): boolean => normalizeCached(query).definitional

/**
 * Split a query into terms.
 *
 * Terms are ANDed, not ORed: "transfer email" must match both words somewhere in the
 * record. OR semantics were tried first and are wrong for a palette — adding a word made
 * result sets grow, so refining a search made it worse, which is the opposite of what
 * typing more is supposed to do.
 *
 * The AND rule is also why this normalises rather than merely splitting: under AND, one
 * word that matches nothing is not a weak signal, it is a veto. See `normalizeQuery`.
 */
export const queryTerms = (query: string): string[] => normalizeCached(query).terms

/**
 * Score a record described as weighted fields.
 *
 * Returns 0 when any term matches nothing (the AND rule above). Otherwise the score is
 * the sum over terms of that term's best field match, plus a bonus when the whole query
 * matches one field as a phrase — so "reader setup" ranks the page called exactly that
 * above one that merely mentions both words apart.
 */
export const scoreFields = (fields: ScorableField[], query: string): number => {
  const { text, terms } = normalizeCached(query)
  if (terms.length === 0) return 0

  let total = 0
  for (const term of terms) {
    let best = 0
    for (const field of fields) {
      best = Math.max(best, matchStrength(field.text, term) * field.weight)
    }
    if (best === 0) return 0 // a term matched nothing — reject the record
    total += best
  }

  if (terms.length > 1) {
    // The NORMALISED text, not the raw query. Scoring the phrase as typed would hand the
    // bonus back to whatever record happens to contain the interrogative words we just
    // removed, which is the bug this normalisation exists to fix.
    let bestPhrase = 0
    for (const field of fields) {
      bestPhrase = Math.max(bestPhrase, matchStrength(field.text, text) * field.weight)
    }
    total += bestPhrase
  }

  return total
}

/**
 * Field weights.
 *
 * Ordered by how much the field says about intent: a title is what a page IS, a tag is
 * what it is filed under, a heading is one section of it, an excerpt is incidental prose
 * that happens to contain the word. Exported so the numbers are inspectable rather than
 * scattered as literals through the scoring calls.
 */
export const WEIGHTS = {
  title: 10,
  tag: 6,
  heading: 5,
  category: 3,
  excerpt: 2,
  /** Commands are matched on their own label + keyword list. */
  commandLabel: 10,
  commandKeyword: 6,
  commandGroup: 2,
  /**
   * Glossary. The TERM outranks a doc title (12 vs 10) on purpose: someone typing a term of art is
   * asking what it means, and the definition is a more precise answer than a guide that merely
   * mentions the word. An alias scores like a title, because "digital certificate" is the same
   * question as "certificate", just phrased differently.
   *
   * `short` is scored low — it is one sentence, so matching a word inside it is weak evidence, and
   * scoring it high would surface every entry whose sentence happens to contain a common word.
   */
  glossaryTerm: 12,
  glossaryAlias: 10,
  glossaryShort: 2,
} as const

/**
 * A ranked search result.
 *
 * `heading` present means the result targets an anchor inside the guide rather than its
 * top — /docs/:category/:slug#:id. Those anchors are assigned at publish time and are
 * what make section-level results possible at all (see docs/index.ts DocHeading).
 */
export interface DocMatch {
  doc: DocEntry
  score: number
  heading?: DocHeading
}

/**
 * The fields that describe the PAGE — everything except its headings.
 *
 * Split out as its own function rather than filtered out of a combined list. The filter
 * that did this before compared field TEXT against every heading's text, which drops a
 * field whenever the two happen to be equal — so a guide titled "Reader setup" that also
 * contains a "Reader setup" section lost its title field. Because `scoreFields` returns 0
 * the moment any term matches nothing, losing the title could zero the whole page score
 * and make the guide's own page row disappear, leaving only anchors. Partitioning by
 * kind cannot express that bug.
 */
const docPageFields = (doc: DocEntry): ScorableField[] => [
  { text: doc.title, weight: WEIGHTS.title },
  { text: doc.headline, weight: WEIGHTS.title },
  { text: doc.category, weight: WEIGHTS.category },
  ...doc.tags.map(t => ({ text: t, weight: WEIGHTS.tag })),
  { text: doc.excerpt, weight: WEIGHTS.excerpt },
]

// There is no matching `docHeadingFields`: headings are scored one at a time in the loop
// below, because each one has to stay paired with the heading it came from to build the
// anchor. A flat list would lose that association.

/** Most anchor rows one guide may contribute, so a long guide cannot flood the list. */
const MAX_ANCHORS_PER_DOC = 3

/**
 * Rank the docs corpus, emitting page results and heading-anchor results.
 *
 * A guide can produce two kinds of row. If the query matches its title or tags, the page
 * itself is a result. If the query matches a heading, that SECTION is a result and links
 * to the anchor. Both can happen for one guide — searching "transfer" should offer both
 * the ownership guide and the specific section inside it.
 *
 * The one case that is collapsed: when a guide matched ONLY through its headings, the
 * page row is dropped and just the anchor rows are kept. Emitting both would put two rows
 * on screen that differ by a fragment the user cannot see, and the more specific one is
 * always the better answer.
 */
export const searchDocEntries = (docs: DocEntry[], query: string, limit = 20): DocMatch[] => {
  if (!query.trim()) return []
  const out: DocMatch[] = []

  for (const doc of docs) {
    const pageScore = scoreFields(docPageFields(doc), query)

    // Each heading is scored against the page's identifying fields too, so a section
    // still matches "certificates transfer" when only "transfer" is in the heading and
    // "certificates" comes from the guide's title.
    const identity: ScorableField[] = [
      { text: doc.title, weight: WEIGHTS.title },
      { text: doc.headline, weight: WEIGHTS.title },
      ...doc.tags.map(t => ({ text: t, weight: WEIGHTS.tag })),
    ]

    const anchors: DocMatch[] = []
    for (const heading of doc.headings) {
      const score = scoreFields([{ text: heading.text, weight: WEIGHTS.heading }, ...identity], query)
      // Require the heading itself to carry some of the match. Without this, every
      // heading in a guide whose TITLE matches would qualify, and one guide would fill
      // the whole result list with sections that have nothing to do with the query.
      const headingCarries = queryTerms(query).some(t => matchStrength(heading.text, t) > 0)
      if (score > 0 && headingCarries) anchors.push({ doc, score, heading })
    }

    anchors.sort((a, b) => b.score - a.score)
    const topAnchors = anchors.slice(0, MAX_ANCHORS_PER_DOC)

    if (pageScore > 0) out.push({ doc, score: pageScore })
    out.push(...topAnchors)
  }

  return out.sort((a, b) => b.score - a.score).slice(0, limit)
}

/**
 * A navigable destination in the palette.
 *
 * `keywords` carry the words a user would plausibly type that are not in the label —
 * "people" for the Owners page, "invoice" for Billing. They are part of the scored
 * surface, not a separate alias lookup, so they rank rather than merely match.
 */
export interface SearchCommand {
  id: string
  label: string
  /** Where the row sits in the list, and part of the scored text. */
  group: string
  path: string
  keywords: string[]
}

export interface CommandMatch {
  command: SearchCommand
  score: number
}

export const searchCommands = (
  commands: SearchCommand[],
  query: string,
  limit = 8,
): CommandMatch[] => {
  if (!query.trim()) return []
  return commands
    .map(command => ({
      command,
      score: scoreFields(
        [
          { text: command.label, weight: WEIGHTS.commandLabel },
          { text: command.group, weight: WEIGHTS.commandGroup },
          ...command.keywords.map(k => ({ text: k, weight: WEIGHTS.commandKeyword })),
        ],
        query,
      ),
    }))
    .filter(m => m.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}

/** A ranked glossary result. `entry.short` IS the definition shown inline in the palette. */
export interface GlossaryMatch {
  entry: GlossaryEntry
  score: number
}

/**
 * Rank glossary entries for the command palette.
 *
 * Scored over the term, its aliases and its one-sentence definition — the same three things a
 * reader might half-remember. Deliberately NOT over `long`: that is glossary-page-only prose, and
 * matching it would put entries in the palette whose visible row (term + `short`) contains none of
 * the words that were typed, which reads as a wrong result.
 */
export const searchGlossaryEntries = (
  entries: readonly GlossaryEntry[],
  query: string,
  limit = 4,
): GlossaryMatch[] => {
  if (!query.trim()) return []
  return entries
    .map(entry => ({
      entry,
      score: scoreFields(
        [
          { text: entry.term, weight: WEIGHTS.glossaryTerm },
          ...(entry.aliases ?? []).map(a => ({ text: a, weight: WEIGHTS.glossaryAlias })),
          { text: entry.short, weight: WEIGHTS.glossaryShort },
        ],
        query,
      ),
    }))
    .filter(m => m.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}
